import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Clock, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2
} from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import CustomSelect from '../shared/CustomSelect';
import { ErrorAlert } from '../shared/ErrorAlert';
import { FormatUtils } from '../shared/FormatUtils';

const MONTHS_OPTIONS = [
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 12, label: '12 months' },
    { value: 24, label: '24 months' },
];

const CleanupStaleRecords = () => {
    const navigate = useNavigate();
    const [months, setMonths] = useState(6);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginating, setPaginating] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchStale = useCallback(async () => {
        try {
            setPaginating(true);
            setError('');
            const response = await dataQualityService.cleanup.stale(months, currentPage, pageSize);

            if (response.success && response.data) {
                const pageData = response.data;
                setClients(pageData.content || []);
                setTotalElements(pageData.totalElements || 0);
                setTotalPages(pageData.totalPages || 0);
            }
        } catch (err) {
            console.error('Error fetching stale records:', err);
            setError('Failed to load stale records');
        } finally {
            setLoading(false);
            setPaginating(false);
        }
    }, [months, currentPage, pageSize]);

    useEffect(() => {
        fetchStale();
    }, [fetchStale]);

    useEffect(() => {
        setCurrentPage(0);
    }, [months]);

    const startIndex = Math.min(currentPage * pageSize + 1, totalElements);
    const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/data-quality/cleanup')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">Stale Records</h2>
                        <p className="text-sm text-gray-500">Clients with no updates in the specified period</p>
                    </div>
                </div>
            </div>

            {/* Filter */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-end gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Threshold</label>
                        <CustomSelect
                            value={months}
                            onChange={(val) => setMonths(Number(val))}
                            options={MONTHS_OPTIONS}
                            placeholder="Select threshold"
                        />
                    </div>
                    <span className="text-sm text-gray-500 pb-2">{totalElements.toLocaleString()} stale clients found</span>
                </div>
            </div>

            {error && <ErrorAlert error={error} onRetry={fetchStale} />}

            {/* Results Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
                {(loading || paginating) && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                {!loading && clients.length === 0 ? (
                    <div className="text-center py-12">
                        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No stale records found for {months} months threshold</p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {clients.map((client) => (
                                <tr key={client.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {client.firstName} {client.lastName || client.companyName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.clientCode}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.clientType}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.email || '-'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs rounded-full ${client.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                            {client.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => navigate(`/clients/${client.id}`)}
                                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Pagination */}
                {(totalPages > 0 || totalElements > 0) && (
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-700">
                                Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                                <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                                <span className="font-medium">{totalElements.toLocaleString()}</span> results
                            </span>
                            <CustomSelect
                                value={pageSize}
                                onChange={(val) => { setPageSize(Number(val)); setCurrentPage(0); }}
                                options={[
                                    { value: 10, label: '10 per page' },
                                    { value: 20, label: '20 per page' },
                                    { value: 50, label: '50 per page' },
                                ]}
                                dropUp
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronsLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">
                                Page {(currentPage + 1).toLocaleString()} of {totalPages.toLocaleString() || 1}
                            </span>
                            <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronsRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CleanupStaleRecords;
